import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/global.css';

// ── Lazy-loaded candidate pages ──────────────────────────────────
const Login     = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));

// ── Loading Spinner ───────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: '#0f172a', zIndex: 9999
    }}>
      <div style={{
        width: '40px', height: '40px',
        border: '3px solid rgba(137, 207, 240,0.2)',
        borderTopColor: '#70BFDF',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Route Guards ─────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user)   return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user)    return <Navigate to="/dashboard" replace />;
  return children;
}

// ── App Routes ───────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        {/* Public — jobs board is accessible without login; login/register page */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

        {/* Semi-public — the dashboard lists public jobs but protected tabs require auth */}
        <Route path="/"         element={<Dashboard />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: "'Inter', sans-serif",
              fontSize: '13.5px',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.06)',
              background: '#1e293b',
              color: '#f8fafc',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
            },
            success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
